import { GENESIS_PROVIDER_ID } from '@lib/api/auth/genesis-provider';
import { resetBridgeTokenProvisioning } from '@lib/commerce/ensureBridgeToken';
import { clearMiniCartCache } from '@lib/commerce/miniCartCache';
import { AUTH_SYNC_COOKIE_NAME } from '@lib/constants/auth';
import { ACTIVE_ORGANIZATION_ID_COOKIE } from '@lib/constants/cookies';
import { resetSearchTokens } from '@lib/coveo/searchTokenHelpers';
import Cookie from 'js-cookie';
import {
  signIn as signInNextAuth,
  signOut as signOutNextAuth,
} from 'next-auth/react';

type Options = {
  /**
   * Used to remove the `auth_sync` Session cookie that's used to sync authentication state betwen ASV2 and Compass UI.
   * The reason we want to clear this cookie is when an automatic log in/out-sequence happen we don't want
   * subsequent page loads to re-try the behavior if the action failed or the user decided to not follow through.
   */
  clearAuthSync: boolean;
};

export const signIn = (options?: Options) => {
  if (options?.clearAuthSync) {
    Cookie.remove(AUTH_SYNC_COOKIE_NAME);
  }
  // clear the anonymous search token from localStorage so an authenticated one can be generated
  resetSearchTokens();
  // The cart a guest cart is about to merge into must be read afresh (CPS-7696).
  clearMiniCartCache();
  return signInNextAuth(GENESIS_PROVIDER_ID);
};

export const signOut = async (options?: Options) => {
  if (options?.clearAuthSync) {
    Cookie.remove(AUTH_SYNC_COOKIE_NAME);
  }

  // clear the authenticated search token from localStorage so an anonymous one can be generated
  resetSearchTokens();

  // The header mini-cart's stored cart reads are the user's (CPS-7696).
  clearMiniCartCache();

  // remove active organization from cookies
  Cookie.remove(ACTIVE_ORGANIZATION_ID_COOKIE);

  const logoutParams = new URLSearchParams({
    post_logout_redirect_uri: (process.env.NEXT_PUBLIC_SITE_URL ?? '') + '/',
  }).toString();

  // callback URL to sign out of Unity ID
  const callbackUrl = `${process.env.NEXT_PUBLIC_UNITY_API}/oauth2/end-session?${logoutParams}`;

  // sign out of next-auth for Compass UI
  const data = await signOutNextAuth({
    callbackUrl,
    redirect: false,
  }).finally(
    // A user bridge token left behind would outlive the session it
    // authorizes. Torn down after NextAuth, not before: a tab that has not
    // synced the sign-out yet is only refused once the server has dropped the
    // session cookie. In `finally`, so a sign-out that failed half-way still
    // drops the tokens.
    resetBridgeTokenProvisioning
  );

  window.location.href = data.url;
};
